<template>
    <Header v-if="mode !== 'no-button'" />
        <section class="rka-test address-form-shell" :class="{ 'no-apartment-step': disableApartment }">
            <Form @submit="save" :id="formId" class="w-100 h-100">
                <TransitionGroup name="address-progress-transition" tag="div" class="address-progress">
                    <template v-for="(step, index) in progressSteps" :key="step.key">
                        <div class="address-progress-step" :class="{ completed: step.completed }">
                            <div class="address-progress-icon">
                                <i v-if="step.completed" class="bi bi-check-lg"></i>
                                <span v-else>{{ step.number }}</span>
                            </div>
                            <span class="address-progress-label">{{ step.label }}</span>
                        </div>
                        <div
                            v-if="index < progressSteps.length - 1"
                            :key="`line-${step.key}`"
                            class="address-progress-line"
                        ></div>
                    </template>
                </TransitionGroup>

                <div class="address-fields" :class="{ 'has-city-secondary': selectedCityRegion }">
                    <div class="address-field address-field-city">
                        <div class="address-field-label">{{ $t('rka-address.city') }}</div>
                        <div
                            class="address-input-with-icon"
                            :class="{ 'has-secondary-text': selectedCityRegion }"
                        >
                            <i class="bi bi-geo-alt"></i>
                            <Select2
                                :key="`city-${instanceId}-${cities.length}`"
                                v-model="cityId"
                                :name="`${fieldNamePrefix}-city`"
                                :options="cityOptions"
                                :placeholder="$t('location.locality-placeholder')"
                                :validations="['required']"
                                :parent="selectParent"
                                dropdown-class="rka-address-select2"
                                :remote-search="searchCities"
                                @change="onCitySelect"
                            />
                            <div class="address-field-secondary" v-if="selectedCityRegion">
                                {{ selectedCityRegion }}
                            </div>
                        </div>
                    </div>

                    <div class="address-field address-field-street">
                        <div class="address-field-label">{{ $t('rka-address.street') }}</div>
                        <div class="address-input-with-icon">
                            <i class="bi bi-geo-alt"></i>
                            <Select2
                                :key="`street-${instanceId}-${cityId || 'empty'}`"
                                v-model="streetId"
                                :name="`${fieldNamePrefix}-street`"
                                :options="streets"
                                :placeholder="$t('location.street-placeholder')"
                                :validations="['required']"
                                :parent="selectParent"
                                :disabled="!cityId"
                                :allowClear="true"
                                dropdown-class="rka-address-select2"
                                :remote-search="searchStreets"
                                @change="onStreetSelect"
                            />
                        </div>
                    </div>

                    <div class="address-field address-field-building">
                        <div class="address-field-label">{{ $t('rka-address.building') }}</div>
                        <div class="address-input-with-icon">
                            <i class="bi bi-geo-alt"></i>
                            <Select2
                                :key="`building-${instanceId}-${streetId || 'empty'}-${buildings.length}`"
                                v-model="buildingId"
                                :name="`${fieldNamePrefix}-buildings`"
                                :options="buildings"
                                :placeholder="$t('location.building-number-placeholder')"
                                :validations="['required']"
                                :parent="selectParent"
                                :disabled="!streetId"
                                :allowClear="true"
                                dropdown-class="rka-address-select2"
                                :remote-search="searchBuildings"
                                @change="onBuildingSelect"
                            />
                        </div>
                    </div>

                    <Transition name="address-apartment-transition">
                        <div class="address-field address-field-apartment" v-if="hasApartmentStep">
                            <div class="address-field-label">{{ $t('rka-address.apartment') }}</div>
                            <div class="address-input-with-icon">
                                <i class="bi bi-geo-alt"></i>
                                <Select2
                                    v-if="cities.length"
                                    :key="`building-part-${instanceId}-${buildingId || 'empty'}-${buildingParts.length}`"
                                    v-model="buildingPartId"
                                    :name="`${fieldNamePrefix}-buildingParts`"
                                    :options="buildingParts"
                                    :placeholder="$t('location.apartment-placeholder')"
                                    :parent="selectParent"
                                    :disabled="!buildingId || disableApartment"
                                    :allowClear="true"
                                    dropdown-class="rka-address-select2"
                                    :remote-search="searchBuildingParts"
                                    @change="onBuildingPartSelect"
                                />
                            </div>
                        </div>
                    </Transition>
                </div>

                <div class="address-extra" v-if="allowExtra" v-show="showExtra">
                    <label class="address-field-label" for="address-extra">{{ $t('rka-address.extra') }}</label>
                    <textarea
                        id="address-extra"
                        v-model="extra"
                        name="extra"
                        class="form-control address-extra-textarea"
                        :placeholder="$t('rka-address.extra-placeholder')"
                        rows="3"
                        :maxlength="maxExtraLength"
                    ></textarea>
                </div>

                <div v-if="showTip" class="address-tip-card">
                    <i class="bi bi-info-circle"></i>
                    <div>
                        <div class="address-tip-title">{{ $t('rka-address.tip-title') }}</div>
                        <div class="address-tip-text">{{ $t('rka-address.tip-text') }}</div>
                    </div>
                </div>

                <div v-if="mode !== 'no-button'" class="mt-3 d-flex justify-content-center align-items-center">
                    <button class="btn btn-primary" type="submit" name="action" :value="mode">
                        {{ $t(`btn.${mode}`) }}
                    </button>
                </div>
            </Form>
        </section>
</template>

<script>
import { Form } from 'vee-validate'
import Select2 from './form/Select2.vue'

export default {
    data: () => ({
        instanceId: `rka-address-${Math.random().toString(36).slice(2, 10)}`,
        cities: [],
        city: null,
        cityId: null,
        streets: [],
        street: null,
        streetId: null,
        buildings: [],
        building: null,
        buildingId: null,
        buildingParts: [],
        buildingPart: null,
        buildingPartId: null,
        fullAddress: null,
        extra: '',
        maxExtraLength: 100,
        selectedCityText: null,
        selectedCityRegion: null,
        selectedStreetText: null,
        selectedBuildingText: null,
        selectedBuildingPartText: null,
        loadingInitialAddress: false
    }),
    components: {
        Form,
        Select2
    },
    computed: {
        formId() {
            return `${this.instanceId}-form`
        },
        fieldNamePrefix() {
            return this.instanceId
        },
        selectParent() {
            return this.parent || `#${this.formId}`
        },
        showExtra() {
            const partIsFilled = 
                this.buildingPart != null &&
                this.buildingPart !== '' &&
                this.buildingPart !== 'null' &&
                this.buildingPart !== undefined

            const noPartsAndBuildingFilled = 
                this.buildingParts.length === 0 &&
                this.building != null &&
                this.building !== '' &&
                this.building !== 'null' &&
                this.building !== undefined

            return true
            return partIsFilled || noPartsAndBuildingFilled
        },
        hasApartmentStep() {
            if(this.disableApartment) return false

            return this.buildingParts.length > 0 || !!this.buildingPartId
        },
        progressSteps() {
            const steps = [
                {
                    key: 'city',
                    number: 1,
                    label: this.$t('rka-address.city'),
                    completed: !!this.cityId
                },
                {
                    key: 'street',
                    number: 2,
                    label: this.$t('rka-address.street'),
                    completed: !!this.streetId
                },
                {
                    key: 'building',
                    number: 3,
                    label: this.$t('rka-address.building'),
                    completed: !!this.buildingId
                }
            ]

            if(this.hasApartmentStep) {
                steps.push({
                    key: 'apartment',
                    number: 4,
                    label: this.$t('rka-address.apartment'),
                    completed: !!this.buildingPartId
                })
            }

            return steps
        },
        cityOptions() {
            return this.normalizeOptions(this.cities)
        }
    },
    props: {
        modelValue: {
            type: String,
            default: null
        },
        kato: {
            type: String,
            default: ''
        },
        building_number: {
            type: String,
            default: ''
        },
        apt: {
            type: String,
            default: ''
        },
        mode: {
            type: String,
            default: "no-button"
        }, 
        parent: {
            type: String,
            default: ''
        },
        allowExtra: {
            type: Boolean,
            default: true
        },
        showTip: {
            type: Boolean,
            default: true
        },
        disableApartment: {
            type: Boolean,
            default: false
        },
        initialAddress: {
            type: Object,
            default: null
        }
    },
    emits: ['update:modelValue', 'saved'],
    async mounted() {
        await this.getCities()
        if(this.kato) await this.setCityByKato(this.kato)
        if(this.hasInitialAddress(this.initialAddress)) await this.loadInitialAddress(this.initialAddress)
    },
    unmounted() {
        // if(this.timer) clearTimeout(this.timer)
    },
    watch: {
        kato: async function() {
            await this.setCityByKato(this.kato)
        },
        initialAddress: {
            async handler(value) {
                if(this.hasInitialAddress(value)) await this.loadInitialAddress(value)
            },
            deep: true
        },
        building() {
            if(this.loadingInitialAddress) return
            if (this.mode === 'no-button') {
                this.returnAddressPartitioned({ notify: false })
            }
        },
        buildingPart() {
            if(this.loadingInitialAddress) return
            if (this.mode === 'no-button') {
                this.returnAddressPartitioned({ notify: false })
            }
        },
        extra() {
            if(this.loadingInitialAddress) return
            if (this.mode === 'no-button') {
                this.returnAddressPartitioned({ notify: false })
            }
        },
        cityId: {
            handler(newValue, oldValue) {
                if(this.loadingInitialAddress) return
                if(newValue !== oldValue) this.clear('streets')
            }
        },
        streetId: {
            handler(newValue, oldValue) {
                if(this.loadingInitialAddress) return
                if(newValue !== oldValue) this.clear('buildings')
            }
        },
        buildingId: {
            handler(newValue, oldValue) {
                if(this.loadingInitialAddress) return
                if(newValue !== oldValue) this.clear('building_parts')
            }
        }
    },
    methods: {
        hasInitialAddress(address) {
            return !!address && Object.keys(address).length > 0
        },
        async loadInitialAddress(address) {
            this.loadingInitialAddress = true

            this.cityId = address.city ?? address.locality_id ?? null
            this.city = address.cityText ?? address.city_text ?? ''
            this.selectedCityText = this.city
            this.selectedCityRegion = address.cityRegion ?? address.city_region ?? null

            if(this.cityId) {
                await this.getStreets(this.cityId)
                const selectedCity = this.cities.find(city => Number(city.id) === Number(this.cityId))
                if(selectedCity && !this.city) {
                    this.city = selectedCity.name_rus || selectedCity.text || ''
                    this.selectedCityText = this.city
                    this.selectedCityRegion = this.getCityRegion(selectedCity)
                }
            }

            this.streetId = address.street ?? null
            this.street = address.streetText ?? address.street_text ?? ''
            this.selectedStreetText = this.street

            if(this.cityId && this.streetId) {
                await this.getBuildings(this.cityId, this.streetId)
                const selectedStreet = this.streets.find(street => Number(street.id) === Number(this.streetId))
                if(selectedStreet && !this.street) {
                    this.street = selectedStreet.text || ''
                    this.selectedStreetText = this.street
                }
            }

            this.buildingId = address.building ?? address.building_number ?? null
            this.building = address.buildingText ?? address.building_text ?? ''
            this.selectedBuildingText = this.building

            if(this.buildingId && !this.disableApartment) {
                await this.getBuildingParts(this.buildingId)
                const selectedBuilding = this.buildings.find(building => Number(building.id) === Number(this.buildingId))
                if(selectedBuilding && !this.building) {
                    this.building = selectedBuilding.text || ''
                    this.selectedBuildingText = this.building
                }
            }

            if(this.disableApartment) {
                this.clear('building_parts')
            } else {
                this.buildingPartId = address.buildingPart ?? address.apartment ?? null
                this.buildingPart = address.buildingPartText ?? address.apartment_text ?? ''
                this.selectedBuildingPartText = this.buildingPart
                const selectedBuildingPart = this.buildingParts.find(buildingPart => Number(buildingPart.id) === Number(this.buildingPartId))
                if(selectedBuildingPart && !this.buildingPart) {
                    this.buildingPart = selectedBuildingPart.text || ''
                    this.selectedBuildingPartText = this.buildingPart
                }
            }
            this.extra = address.extra ?? ''
            this.loadingInitialAddress = false

            if(this.mode === 'no-button') {
                this.returnAddressPartitioned()
            }
        },
        async getCities(filter = '') {
            let response = await this.$api.getCities(filter)
            if(response.result === 'SUCCESS') {
                this.cities = response.data
            }
        },
        async getStreets(city) {
            let response = await this.$api.getRKAStreets(city)
            // console.log('response', response)
            if(response.result === 'SUCCESS') {
                this.streets = response.data
            }
        },
        async getBuildings(city, street) {
            let response = await this.$api.getRKABuildings(city, street) // /location/rka-buildings
            if(response.result === 'SUCCESS') {
                this.buildings = response.data
            }
        },
        async getBuildingParts(building) {
            let response = await this.$api.getRKABuildingParts(building)
            if(response.result === 'SUCCESS') {
                this.buildingParts = response.data
            }
        },
        async searchCities(term = '') {
            let response = await this.$api.getCities(term)

            if(response.result !== 'SUCCESS') return []

            return this.normalizeOptions(response.data)
        },
        async searchStreets(term = '') {
            if(!this.cityId) return []

            let response = await this.$api.getRKAStreets(this.cityId, term)

            if(response.result !== 'SUCCESS') return []

            return this.normalizeOptions(response.data)
        },
        async searchBuildings(term = '') {
            if(!this.cityId || !this.streetId) return []

            let response = await this.$api.getRKABuildings(this.cityId, this.streetId, term)

            if(response.result !== 'SUCCESS') return []

            return this.normalizeOptions(response.data)
        },
        async searchBuildingParts(term = '') {
            if(!this.buildingId) return []

            let response = await this.$api.getRKABuildingParts(this.buildingId, term)

            if(response.result !== 'SUCCESS') return []

            return this.normalizeOptions(response.data)
        },
        async getAddress() {
            let response = await this.$api.getRKAAddress(this.cityId, this.streetId, this.buildingId, this.buildingPartId)
            if(response.result === 'SUCCESS') {
                this.fullAddress = response.data.full_path_rus
            }
        },
        normalizeOptions(options = []) {
            return options.map((option) => ({
                ...option,
                text: option.text || option.name_rus || option.name || option.number || ''
            }))
        },
        getCityRegion(item) {
            return item?.region_rus || item?.region || item?.oblast_rus || item?.oblast || item?.area_rus || item?.area || item?.parent_name_rus || item?.parent_name || item?.full_path_rus || null
        },
        getSelectedItem(id, options = [], text = '', meta = null) {
            const item = meta?.item || options.find((option) => String(option.id) === String(id))

            if(item) return item

            return {
                id,
                text
            }
        },
        async onCitySelect(id, text, meta) {
            if(!id) {
                this.city = null
                this.selectedCityText = null
                this.selectedCityRegion = null
                this.clear('streets')
                return
            }

            return this.cityDatalistClicked(this.getSelectedItem(id, this.cityOptions, text, meta))
        },
        async onStreetSelect(id, text, meta) {
            if(!id) {
                this.street = null
                this.clear('buildings')
                return
            }

            return this.streetDatalistClicked(this.getSelectedItem(id, this.streets, text, meta))
        },
        async onBuildingSelect(id, text, meta) {
            if(!id) {
                this.building = null
                this.clear('building_parts')
                return
            }

            return this.buildingDatalistClicked(this.getSelectedItem(id, this.buildings, text, meta))
        },
        async onBuildingPartSelect(id, text, meta) {
            if(!id) {
                this.buildingPart = null
                this.buildingPartId = null
                if (this.mode === 'no-button') {
                    this.returnAddressPartitioned({ notify: false })
                }
                return
            }

            return this.buildingPartDatalistClicked(this.getSelectedItem(id, this.buildingParts, text, meta))
        },
        async cityDatalistClicked(item) {
            if(this.cityId && this.cityId !== item.id) this.clear('streets')

            this.city = item.name_rus || item.text
            this.cityId = item.id
            this.selectedCityText = this.city
            this.selectedCityRegion = this.getCityRegion(item)

            // The street Select2 performs its own empty-term remote search
            // when opened. Preloading here changes `streets`, which makes the
            // shared Select2 wrapper reinitialize and close an open dropdown.
            await this.getAddress()
        },
        async streetDatalistClicked(item) {
            this.street = item.text
            this.streetId = item.id
            this.selectedStreetText = item.text
            await this.getBuildings(this.cityId, item.id)
            await this.getAddress()
        },
        async buildingDatalistClicked(item) {
            this.building = item.text
            this.buildingId = item.id
            this.selectedBuildingText = item.text
            if(this.disableApartment) {
                this.clear('building_parts')
            } else {
                await this.getBuildingParts(item.id)
            }
            if (this.mode === 'no-button' && (this.disableApartment || this.buildingParts.length === 0)) {
                this.returnAddressPartitioned({ notify: false })
            }
            await this.getAddress()
        },
        async buildingPartDatalistClicked(item) {
            this.buildingPart = item.text
            this.buildingPartId = item.id
            this.selectedBuildingPartText = item.text
            if (this.mode === 'no-button') {
                this.returnAddressPartitioned({ notify: false })
            }
            await this.getAddress()
        },
        returnAddressPartitioned(options = {}) {
            const { notify = true } = options

            if(!this.checkExtraLength()) return

            const hasCity = this.hasRkaId(this.cityId)
            const hasStreet = this.hasRkaId(this.streetId)
            const hasBuilding = this.hasRkaId(this.buildingId)

            if (!hasCity || !hasStreet || !hasBuilding) {
                if(notify) {
                    this.$notify({
                        title: 'ERROR',
                        text: 'Incomplete address: city, street, or building missing',
                        type: 'error'
                    })
                }
                this.$emit('update:modelValue', { completed: false })
                return
            }
            this.$emit('update:modelValue', {
                city: this.cityId,
                street: this.streetId,
                building: this.buildingId,
                buildingPart: this.buildingPartId,
                completed: true,
                extra: this.extra
            })
        },
        hasRkaId(value) {
            if(value == null) return false

            const id = String(value).trim()

            return id !== '' && id !== '0' && id.toLowerCase() !== 'null'
        },
        async save(values, actions) {
            const action = event?.submitter?.value

            if (action === 'search') {
                this.returnAddressPartitioned()

                return
            }

            if (action === 'save') {
                await this.saveData()

                return
            }
        },
        async saveData() {
            if(!this.checkExtraLength()) return

            if(!this.buildingPartId && !this.buildingId) {
                this.$notify({
                    title: 'ERROR',
                    text: 'No apt or building',
                    type: 'error'
                })

                return
            }
            this.$emit('saved', this.city, this.street, this.building, this.buildingPart, this.extra)
        },
        checkExtraLength() {
            if(!this.extra || this.extra.length <= this.maxExtraLength) return true

            this.$notify({
                title: 'ERROR',
                text: `Extra must be ${this.maxExtraLength} characters or less`,
                type: 'error'
            })

            return false
        },
        clear(str) {
            if(str === 'streets') {
                this.streets = []
                this.street = null
                this.streetId = null
                this.selectedStreetText = null
                this.selectedCityRegion = this.cityId ? this.selectedCityRegion : null
                this.clear('buildings')
            } else if (str === 'buildings') {
                this.buildings = []
                this.building = null
                this.buildingId = null
                this.selectedBuildingText = null
                this.clear('building_parts')
            } else if (str === 'building_parts') {
                this.buildingParts = []
                this.buildingPart = null
                this.buildingPartId = null
                this.selectedBuildingPartText = null
            }
        }
    }
}
</script>

<style scoped>
.address-form-shell {
    width: 100%;
}

.address-progress {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin: 0 0 28px;
}

.address-progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--kdp-space-2);
    min-width: 112px;
    text-align: center;
}

.address-progress-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 2px solid var(--kdp-primary-dark);
    border-radius: 50%;
    background: var(--kdp-white);
    color: var(--kdp-primary-dark);
    font-size: 15px;
    font-weight: var(--kdp-font-weight-bold);
}

.address-progress-step.completed .address-progress-icon {
    background: var(--kdp-primary-dark);
    color: var(--kdp-white);
}

.address-progress-label {
    color: #4b465c;
    font-size: 13px;
    font-weight: var(--kdp-font-weight-semibold);
    line-height: var(--kdp-line-height-snug);
}

.address-progress-line {
    width: 100px;
    height: 2px;
    margin: 18px 8px 0;
    background: var(--kdp-primary-dark);
}

.address-progress-transition-move,
.address-progress-transition-enter-active,
.address-progress-transition-leave-active {
    transition: opacity .44s ease, transform .44s ease, width .44s ease, margin .44s ease;
}

.address-progress-transition-enter-from,
.address-progress-transition-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(.96);
}

.address-progress-transition-leave-active {
    position: absolute;
}

.address-fields {
    display: grid;
    grid-template-columns: minmax(260px, 3fr) minmax(300px, 5fr) minmax(260px, 3fr);
    align-items: flex-start;
    gap: var(--kdp-space-4);
    width: 100%;
}

.address-field {
    min-width: 0;
    width: 100%;
}

.address-field-apartment {
    grid-column: 1;
}

.address-field-label {
    display: block;
    margin-bottom: var(--kdp-space-2);
    color: var(--kdp-text-secondary);
    background: transparent;
    font-size: 14px;
    font-weight: var(--kdp-font-weight-semibold);
}

.address-input-with-icon {
    position: relative;
    width: 100%;
}

.address-input-with-icon > .bi {
    position: absolute;
    top: 19px;
    left: 14px;
    z-index: 2;
    color: var(--kdp-primary-dark);
    font-size: 18px;
    pointer-events: none;
    transition: top .36s ease;
}

.address-input-with-icon :deep(.input-group) {
    display: block;
    margin-bottom: 0;
    width: 100%;
}

.address-input-with-icon :deep(.select-group) {
    margin-bottom: 0 !important;
    width: 100%;
}

.address-input-with-icon :deep(.form-control) {
    display: block;
    width: 100%;
    min-height: 58px;
    border: 1px solid #d7d2fb;
    border-radius: 14px;
    padding-left: 42px;
    color: var(--kdp-text-dark);
    transition: min-height .36s ease, padding .36s ease, border-color .36s ease, box-shadow .36s ease;
}

.address-input-with-icon :deep(.form-control:focus) {
    border-color: var(--kdp-primary);
    box-shadow: 0 0 0 .2rem rgba(166, 160, 251, .18);
}

.address-input-with-icon :deep(.select2-selection.select2-selection--single) {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 58px;
    border: 1px solid #d7d2fb;
    border-radius: 14px;
    padding: 0 42px;
    color: var(--kdp-text-dark);
    transition: min-height .36s ease, padding .36s ease, border-color .36s ease, box-shadow .36s ease;
}

.address-input-with-icon :deep(.select2-container--default.select2-container--focus .select2-selection) {
    border-color: var(--kdp-primary);
    box-shadow: 0 0 0 .2rem rgba(166, 160, 251, .18);
    outline: none;
}

.address-input-with-icon :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    width: 100%;
    padding: 0;
    color: var(--kdp-text-dark);
    line-height: var(--kdp-line-height-normal);
}

.address-input-with-icon :deep(.select2-container--default .select2-selection--single .select2-selection__placeholder) {
    color: #6c757d;
}

.address-input-with-icon :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    top: 9px;
    right: 12px;
}

.address-input-with-icon :deep(.tip) {
    display: none;
}

.address-input-with-icon.has-secondary-text > .bi {
    top: 18px;
}

.address-input-with-icon.has-secondary-text :deep(.form-control) {
    min-height: 70px;
    padding-top: var(--kdp-space-3);
    padding-bottom: 28px;
}

.address-input-with-icon.has-secondary-text :deep(.select2-selection.select2-selection--single) {
    min-height: 70px;
    padding-top: var(--kdp-space-1);
    padding-bottom: var(--kdp-space-6);
}

.address-fields.has-city-secondary .address-field-street .address-input-with-icon > .bi,
.address-fields.has-city-secondary .address-field-building .address-input-with-icon > .bi {
    top: 25px;
}

.address-fields.has-city-secondary .address-field-street :deep(.form-control),
.address-fields.has-city-secondary .address-field-building :deep(.form-control) {
    min-height: 70px;
}

.address-fields.has-city-secondary .address-field-street :deep(.select2-selection.select2-selection--single),
.address-fields.has-city-secondary .address-field-building :deep(.select2-selection.select2-selection--single) {
    min-height: 70px;
}

.address-field-secondary {
    position: absolute;
    left: 42px;
    right: 14px;
    bottom: 11px;
    z-index: 2;
    color: #8c8798;
    font-size: 12px;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
}

.address-apartment-transition-enter-active,
.address-apartment-transition-leave-active {
    overflow: hidden;
    transition: opacity .44s ease, transform .44s ease, max-height .44s ease, margin .44s ease;
}

.address-apartment-transition-enter-from,
.address-apartment-transition-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-8px);
}

.address-apartment-transition-enter-to,
.address-apartment-transition-leave-from {
    max-height: 120px;
    opacity: 1;
    transform: translateY(0);
}

.address-extra {
    margin-top: 18px;
}

.address-extra-textarea {
    width: 100%;
    min-height: 96px;
    resize: vertical;
    border: 1px solid #d7d2fb;
    border-radius: 14px;
}

.address-extra-textarea:focus {
    border-color: var(--kdp-primary);
    box-shadow: 0 0 0 .2rem rgba(166, 160, 251, .18);
}

.address-tip-card {
    display: flex;
    gap: var(--kdp-space-3);
    width: 100%;
    margin-top: 18px;
    padding: var(--kdp-space-4);
    border-radius: 16px;
    background: #f4f2ff;
    color: #4b465c;
}

.address-tip-card > .bi {
    color: var(--kdp-primary-dark);
    font-size: 20px;
    line-height: var(--kdp-line-height-snug);
}

.address-tip-title {
    color: var(--kdp-text-dark);
    font-size: 14px;
    font-weight: var(--kdp-font-weight-bold);
}

.address-tip-text {
    margin-top: 2px;
    color: var(--kdp-text-muted);
    font-size: 13px;
}

@media (max-width: 991.98px) {
    .address-progress {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        overflow-x: auto;
        justify-content: flex-start;
        padding-bottom: 6px;
    }

    .address-field-city,
    .address-field-street,
    .address-field-building,
    .address-field-apartment {
        grid-column: 1;
    }

    .address-fields {
        grid-template-columns: 1fr;
    }
}
</style>

<style>
.rka-address-select2 .select2-results > .select2-results__options {
    max-height: 360px;
}

.rka-address-select2.select2-dropdown {
    z-index: 1060;
    pointer-events: auto;
}
</style>
